import { useEffect, useState } from "react";
import { endpoints } from "../api/endpoints";
import { useApi, useFreshness } from "../api/hooks";
import type { Premises, Proposal } from "../api/types";
import { can, canStep } from "../auth/roles";
import { ActionDialog, type FieldSpec } from "../components/ActionDialog";
import { StatusBadge } from "../components/Badges";
import { PendingProposalLine, ProposalDialog, ProposedResult } from "../components/FourEyes";
import {
  APPLICATION_STATUS,
  CERTIFICATE_STATUS,
  INSPECTION_KIND,
  INSPECTION_OUTCOME,
  OWNER_KIND,
  PREMISES_CERTIFICATE,
  PREMISES_STATUS,
  PROVENANCE_SOURCE,
  labelOf,
  optionsOf,
  statusOf,
} from "../components/labels";
import { Notice, QueryState, StaleNotice } from "../components/ProblemAlert";
import { useFireContext } from "../context";
import { formatDate, formatDateTime, shortId } from "../format/datetime";
import { showMoney } from "../format/money";
import { lonLat, reasonError, Row, ScreenHeader, useConfigNames, useWardNames } from "./shared";
import { Button, IconButton, IconClose } from "@lcc/ui";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const ownerText = (o: { kind: string; party_id: string }) => `${labelOf(OWNER_KIND, o.kind)} ${shortId(o.party_id)}`;

function coords(lon: string | undefined, lat: string | undefined): { lon: number; lat: number } | string {
  const x = Number(lon);
  const y = Number(lat);
  if (!lon?.trim() || !lat?.trim() || !Number.isFinite(x) || !Number.isFinite(y) || x < -180 || x > 180 || y < -90 || y > 90)
    return "Give longitude (-180 to 180) and latitude (-90 to 90).";
  return { lon: x, lat: y };
}

type DialogState =
  | { kind: "register" }
  | { kind: "edit"; p: Premises }
  | { kind: "risk"; p: Premises }
  | { kind: "occupancy"; p: Premises }
  | { kind: "owner"; p: Premises }
  | { kind: "apply"; p: Premises }
  | { kind: "proposal"; proposal: Proposal }
  | null;

function PremisesDetailPanel({ premisesId, onDialog, proposed, onClose }: { premisesId: string; onDialog: (d: DialogState) => void; proposed: Proposal | null; onClose?: () => void }) {
  const { principal, go } = useFireContext();
  const names = useConfigNames();
  const wardName = useWardNames();
  const q = useApi(["premises", premisesId], (api) => endpoints.getPremises(api, premisesId));
  const approvals = useApi(["approvals", "all", "pending"], (api) => endpoints.listApprovals(api, { mine: "all", status: "pending" }), { enabled: can(principal, "viewApprovals") });
  const p = q.data;
  const fresh = useFreshness(q);
  if (!p) return <QueryState isLoading={q.isLoading} error={q.error} label="the premises" />;
  const pending = (approvals.data?.items ?? []).filter((x) => x.subject_id === p.premises_id);
  return (
    <section className="fer-panel fer-record" aria-label={`Premises ${p.name}`}>
      <div className="lcc-detail__head">
        <div className="lcc-detail__titles">
          <h3 className="lcc-detail__title">{p.name}</h3>
          <span className="fer-badges fer-record__status">
            <StatusBadge status={statusOf(PREMISES_CERTIFICATE, p.certificate_status)} />
            <StatusBadge status={statusOf(PREMISES_STATUS, p.status)} />
          </span>
        </div>
        {onClose && <IconButton label="Close" size="sm" icon={<IconClose size={16} />} onClick={onClose} />}
      </div>
      <StaleNotice {...fresh} error={q.error} what="premises record" />
      {proposed && <ProposedResult proposal={proposed} />}
      {pending.map((x) => (
        <PendingProposalLine key={x.proposal_id} proposal={x} onOpen={() => onDialog({ kind: "proposal", proposal: x })} />
      ))}
      <dl className="fer-dl">
        <Row term="Address">{p.address ?? "—"}</Row>
        <Row term="Ward">{wardName(p.ward_id)}</Row>
        <Row term="Position">
          {lonLat(p.lon, p.lat)}
          {p.plus_code ? ` (${p.plus_code})` : ""}
        </Row>
        <Row term="Occupancy">{names.occupancy(p.occupancy_type)}</Row>
        <Row term="Risk class">{names.riskClass(p.risk_class)}</Row>
        <Row term="Owner">
          {ownerText(p.owner)}
          {p.contact_party_id ? `, contact person ${shortId(p.contact_party_id)}` : ""}
        </Row>
        <Row term="Recorded from">
          {p.provenance ? `${labelOf(PROVENANCE_SOURCE, p.provenance.source)}${p.provenance.reference ? ` (${p.provenance.reference})` : ""}` : "—"}, {formatDateTime(p.created_at)}
        </Row>
      </dl>
      <div className="fer-actions fer-actions--start" role="group" aria-label="Premises actions">
        {can(principal, "updatePremises") && (
          <Button variant="secondary" onClick={() => onDialog({ kind: "edit", p })}>
            Correct the details
          </Button>
        )}
        {canStep(principal, "risk_class_change", "propose") && (
          <Button variant="secondary" onClick={() => onDialog({ kind: "risk", p })}>
            Propose a risk-class change
          </Button>
        )}
        {canStep(principal, "occupancy_change", "propose") && (
          <Button variant="secondary" onClick={() => onDialog({ kind: "occupancy", p })}>
            Propose an occupancy change
          </Button>
        )}
        {canStep(principal, "owner_change", "propose") && (
          <Button variant="secondary" onClick={() => onDialog({ kind: "owner", p })}>
            Propose an owner change
          </Button>
        )}
        {can(principal, "applyForCertificate") && !p.open_application && (
          <Button onClick={() => onDialog({ kind: "apply", p })}>
            Apply for a certificate for the owner
          </Button>
        )}
      </div>
      <h4>Certificate</h4>
      {p.current_certificate ? (
        <p>
          {p.current_certificate.number} <StatusBadge status={statusOf(CERTIFICATE_STATUS, p.current_certificate.status)} /> valid{" "}
          {formatDate(p.current_certificate.valid_from)} to {formatDate(p.current_certificate.valid_to)}{" "}
          {can(principal, "viewCertificates") && (
            <button type="button" className="fer-link" onClick={() => go("certificates", { certificateId: p.current_certificate!.certificate_id })}>
              Open the certificate
            </button>
          )}
        </p>
      ) : (
        <p className="fer-muted">No certificate.</p>
      )}
      <h4>Open application</h4>
      {p.open_application ? (
        <p>
          {p.open_application.reference} <StatusBadge status={statusOf(APPLICATION_STATUS, p.open_application.status)} /> fee {showMoney(p.open_application.fee)}{" "}
          {can(principal, "viewApplications") && (
            <button type="button" className="fer-link" onClick={() => go("certificates", { applicationId: p.open_application!.application_id })}>
              Open the application
            </button>
          )}
        </p>
      ) : (
        <p className="fer-muted">None.</p>
      )}
      <h4>Recent inspections</h4>
      {p.recent_inspections.length === 0 ? (
        <p className="fer-muted">None.</p>
      ) : (
        <ul className="fer-plain-list">
          {p.recent_inspections.map((i) => (
            <li key={i.inspection_id}>
              {formatDateTime(i.captured_at)}: {labelOf(INSPECTION_KIND, i.kind)} <StatusBadge status={statusOf(INSPECTION_OUTCOME, i.outcome)} />
              {i.defects && i.defects.length ? ` – ${i.defects.length} defect(s)` : ""}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * The premises register (fire officers their wards; chief and admin city-wide): register and correct premises;
 * changes that matter for a fee or certificate (risk class, occupancy, owner) are four-eyes proposals; a fire officer
 * applies for a certificate on the owner's behalf.
 */
export function PremisesScreen() {
  const { principal, focus, go } = useFireContext();
  const names = useConfigNames();
  const wardName = useWardNames();
  const [q, setQ] = useState("");
  const [cert, setCert] = useState("");
  const [selected, setSelected] = useState<string | null>(focus.premisesId ?? null);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [proposed, setProposed] = useState<Proposal | null>(null);
  useEffect(() => {
    if (focus.premisesId) setSelected(focus.premisesId);
  }, [focus.premisesId]);
  const list = useApi(["premises-list", q.trim().length >= 2 ? q.trim() : "", cert], (api) => endpoints.listPremises(api, { q, certificateStatus: cert || undefined }), { keepPrevious: true });
  const rows = list.data ?? [];
  const occupancyOptions = (names.staff?.occupancy_types ?? []).map((o) => [o.key, names.occupancy(o.key)] as const);
  const riskOptions = (names.staff?.risk_classes ?? []).map((r) => [r.key, `${names.riskClass(r.key)} (fee ${showMoney(r.certificate_fee)})`] as const);
  const ownerFields: FieldSpec[] = [
    { name: "owner_kind", label: "Owner is a", kind: "select", required: true, options: optionsOf(OWNER_KIND) },
    { name: "owner_party_id", label: "Owner's party id", required: true, maxLength: 36 },
  ];
  const onProposed = (p: unknown) => setProposed(p as Proposal);

  return (
    <div className="fer-screen">
      <ScreenHeader title="Premises">
        {can(principal, "registerPremises") && (
          <Button onClick={() => setDialog({ kind: "register" })}>
            Register premises
          </Button>
        )}
      </ScreenHeader>
      <div className="fer-toolbar">
        <label className="fer-field fer-field--inline">
          <span>Name or address</span>
          <input value={q} maxLength={100} onChange={(e) => setQ(e.target.value)} />
        </label>
        <label className="fer-field fer-field--inline">
          <span>Certificate</span>
          <select value={cert} onChange={(e) => setCert(e.target.value)}>
            <option value="">All</option>
            {Object.entries(PREMISES_CERTIFICATE).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <QueryState isLoading={list.isLoading} error={list.error} label="premises" />
      {list.data && rows.length === 0 && <p className="fer-muted">No premises match.</p>}
      <div className={`fer-split${selected && can(principal, "viewPremisesDetail") ? " fer-split--open" : ""}`}>
        <div className="fer-split__main">
          {rows.length > 0 && (
            <table className="lcc-table" aria-label={`Premises (${rows.length})`}>
              <thead>
                <tr>
                  <th scope="col">Premises</th>
                  <th scope="col">Ward</th>
                  <th scope="col" className="fer-col-opt">Occupancy</th>
                  <th scope="col" className="fer-col-opt">Risk class</th>
                  <th scope="col">Certificate</th>
                  <th scope="col">
                    <span className="lcc-sr-only">Open</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.premises_id} className={p.premises_id === selected ? "fer-row--selected" : undefined}>
                    <th scope="row">
                      {p.name}
                      {p.address ? <span className="fer-muted"> – {p.address}</span> : null}
                    </th>
                    <td>{wardName(p.ward_id)}</td>
                    <td className="fer-col-opt">{names.occupancy(p.occupancy_type)}</td>
                    <td className="fer-col-opt">{names.riskClass(p.risk_class)}</td>
                    <td>
                      <StatusBadge status={statusOf(PREMISES_CERTIFICATE, p.certificate_status)} />
                    </td>
                    <td>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setProposed(null);
                          setSelected(p.premises_id);
                        }}
                        aria-label={`Open ${p.name}`}
                      >
                        Open
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {selected && can(principal, "viewPremisesDetail") && (
          <div className="fer-detailpane">
            <PremisesDetailPanel key={selected} premisesId={selected} onDialog={setDialog} proposed={proposed} onClose={() => setSelected(null)} />
          </div>
        )}
      </div>

      {dialog?.kind === "proposal" && <ProposalDialog proposal={dialog.proposal} onClose={() => setDialog(null)} />}
      {dialog?.kind === "register" && (
        <ActionDialog
          title="Register premises"
          intent="registerPremises:new"
          intro={<p>The ward comes from the position (a fire officer registers only in their wards). The risk class defaults to the occupancy type's.</p>}
          fields={[
            { name: "name", label: "Name", required: true, maxLength: 200 },
            { name: "address", label: "Address", maxLength: 300 },
            { name: "lon", label: "Longitude", required: true },
            { name: "lat", label: "Latitude", required: true },
            { name: "occupancy_type", label: "Occupancy type", kind: "select", required: true, options: occupancyOptions },
            { name: "risk_class", label: "Risk class (empty = the occupancy type's default)", kind: "select", options: riskOptions },
            ...ownerFields,
            { name: "provenance_source", label: "Where the record comes from", kind: "select", required: true, options: optionsOf(PROVENANCE_SOURCE).filter(([k]) => k !== "owner_application") },
            { name: "provenance_reference", label: "Reference (book and page, file and row)", maxLength: 200 },
          ]}
          submitLabel="Register"
          build={(v) => {
            if (v.name.trim().length < 2) return { error: "The name has at least 2 characters." };
            const c = coords(v.lon, v.lat);
            if (typeof c === "string") return { error: c };
            if (!UUID.test(v.owner_party_id.trim())) return { error: "The owner's party id is a UUID (36 characters)." };
            const body: Record<string, unknown> = {
              name: v.name.trim(),
              address: v.address?.trim() || null,
              ...c,
              occupancy_type: v.occupancy_type,
              owner: { kind: v.owner_kind, party_id: v.owner_party_id.trim() },
              provenance: { source: v.provenance_source, reference: v.provenance_reference?.trim() || null },
            };
            if (v.risk_class) body.risk_class = v.risk_class;
            return { body };
          }}
          write={(api, body, key) => endpoints.registerPremises(api, body as never, key)}
          onDone={(p) => setSelected((p as Premises).premises_id)}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "edit" && (
        <ActionDialog
          title={`Correct ${dialog.p.name}`}
          intro={
            <Notice>
              A risk-class, occupancy or owner change that matters for a fee or certificate is refused here: propose it instead (a second person
              confirms).
            </Notice>
          }
          initial={{
            name: dialog.p.name,
            address: dialog.p.address ?? "",
            lon: String(dialog.p.lon),
            lat: String(dialog.p.lat),
            occupancy_type: dialog.p.occupancy_type,
            risk_class: dialog.p.risk_class,
            owner_kind: dialog.p.owner.kind,
            owner_party_id: dialog.p.owner.party_id,
            status: dialog.p.status,
          }}
          fields={[
            { name: "name", label: "Name", required: true, maxLength: 200 },
            { name: "address", label: "Address", maxLength: 300 },
            { name: "lon", label: "Longitude", required: true },
            { name: "lat", label: "Latitude", required: true },
            { name: "occupancy_type", label: "Occupancy type", kind: "select", required: true, options: occupancyOptions },
            { name: "risk_class", label: "Risk class", kind: "select", required: true, options: riskOptions },
            ...ownerFields,
            { name: "status", label: "Status", kind: "select", required: true, options: optionsOf(PREMISES_STATUS) },
          ]}
          submitLabel="Save"
          build={(v) => {
            const p = dialog.p;
            const body: Record<string, unknown> = {};
            if (v.name.trim() !== p.name) body.name = v.name.trim();
            if ((v.address?.trim() || null) !== (p.address ?? null)) body.address = v.address?.trim() || null;
            const c = coords(v.lon, v.lat);
            if (typeof c === "string") return { error: c };
            if (c.lon !== p.lon || c.lat !== p.lat) Object.assign(body, c);
            if (v.occupancy_type !== p.occupancy_type) body.occupancy_type = v.occupancy_type;
            if (v.risk_class !== p.risk_class) body.risk_class = v.risk_class;
            if (v.owner_kind !== p.owner.kind || v.owner_party_id.trim() !== p.owner.party_id) {
              if (!UUID.test(v.owner_party_id.trim())) return { error: "The owner's party id is a UUID (36 characters)." };
              body.owner = { kind: v.owner_kind, party_id: v.owner_party_id.trim() };
            }
            if (v.status !== p.status) body.status = v.status;
            return Object.keys(body).length ? { body } : { error: "Nothing has changed." };
          }}
          write={(api, body) => endpoints.updatePremises(api, dialog.p.premises_id, body as never)}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "risk" && (
        <ActionDialog
          title={`Propose a risk-class change for ${dialog.p.name}`}
          intent={`changePremisesRiskClass:${dialog.p.premises_id}`}
          intro={<p>Now: {names.riskClass(dialog.p.risk_class)}. An unpaid fee is replaced at the new class's tariff when a second person confirms.</p>}
          fields={[
            { name: "risk_class", label: "New risk class", kind: "select", required: true, options: riskOptions.filter(([k]) => k !== dialog.p.risk_class) },
            { name: "reason", label: "Reason (at least 3 characters)", kind: "textarea", required: true, maxLength: 1000 },
          ]}
          submitLabel="Propose"
          build={(v) => {
            const e = reasonError(v.reason ?? "");
            return e ? { error: e } : { body: { action: "propose", risk_class: v.risk_class, reason: v.reason.trim() } };
          }}
          write={(api, body, key) => endpoints.changeRiskClass(api, dialog.p.premises_id, body as never, key)}
          onDone={onProposed}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "occupancy" && (
        <ActionDialog
          title={`Propose an occupancy change for ${dialog.p.name}`}
          intent={`changePremisesOccupancy:${dialog.p.premises_id}`}
          intro={<p>Now: {names.occupancy(dialog.p.occupancy_type)}. A valid certificate stays valid to its end.</p>}
          fields={[
            { name: "occupancy_type", label: "New occupancy type", kind: "select", required: true, options: occupancyOptions.filter(([k]) => k !== dialog.p.occupancy_type) },
            { name: "reason", label: "Reason (at least 3 characters)", kind: "textarea", required: true, maxLength: 1000 },
          ]}
          submitLabel="Propose"
          build={(v) => {
            const e = reasonError(v.reason ?? "");
            return e ? { error: e } : { body: { action: "propose", occupancy_type: v.occupancy_type, reason: v.reason.trim() } };
          }}
          write={(api, body, key) => endpoints.changeOccupancy(api, dialog.p.premises_id, body as never, key)}
          onDone={onProposed}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "owner" && (
        <ActionDialog
          title={`Propose an owner change for ${dialog.p.name}`}
          intent={`changePremisesOwner:${dialog.p.premises_id}`}
          intro={<p>Now: {ownerText(dialog.p.owner)}. An unpaid fee is billed to the new owner when a second person confirms.</p>}
          fields={[...ownerFields, { name: "reason", label: "Reason (at least 3 characters)", kind: "textarea", required: true, maxLength: 1000 }]}
          submitLabel="Propose"
          build={(v) => {
            if (!UUID.test((v.owner_party_id ?? "").trim())) return { error: "The owner's party id is a UUID (36 characters)." };
            const e = reasonError(v.reason ?? "");
            return e ? { error: e } : { body: { action: "propose", owner: { kind: v.owner_kind, party_id: v.owner_party_id.trim() }, reason: v.reason.trim() } };
          }}
          write={(api, body, key) => endpoints.changeOwner(api, dialog.p.premises_id, body as never, key)}
          onDone={onProposed}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "apply" && (
        <ActionDialog
          title={`Apply for a fire certificate for ${dialog.p.name}`}
          intent={`applyForCertificate:${dialog.p.premises_id}`}
          intro={<p>The owner is billed the certificate fee of the premises' risk class ({names.riskClass(dialog.p.risk_class)}); an unpaid application lapses.</p>}
          fields={[{ name: "note", label: "Note", kind: "textarea", maxLength: 1000 }]}
          submitLabel="Apply"
          build={(v) => ({ body: v.note?.trim() ? { premises_id: dialog.p.premises_id, note: v.note.trim() } : { premises_id: dialog.p.premises_id } })}
          write={(api, body, key) => endpoints.applyForCertificate(api, body as never, key)}
          onDone={(a) => go("certificates", { applicationId: (a as { application_id: string }).application_id })}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}
